import config from "@Config/index";
import getFeaturedImage from "@Utils/getFeaturedImage";
import getImagePath from "@Utils/getImagePath";
import urlBuilder from "@Utils/urlBuilder";
import { publishWebState } from "@Utils/webApputils/nativeStateBridge";

export const hasRoomTypeFilter = (filters: any = {}) => {
  const roomType = filters?.meta_types ?? filters?.room_types ?? filters?.roomType;
  if (Array.isArray(roomType)) return roomType.filter(Boolean).length > 0;
  return Boolean(roomType);
};

export const withActiveRoomTypesMethod = (methods: any, enabled: boolean) => {
  if (!enabled) return methods;
  const list = (Array.isArray(methods) ? methods : String(methods || "").split(","))
    .flat(Infinity)
    .map((item: any) => String(item).trim())
    .filter(Boolean);
  if (!list.includes("active_room_types")) {
    list.push("active_room_types");
  }
  return list.join(",");
};

export const filter_keys = [
  "p",
  "limit",
  "only",
  "methods",
  "except",
  "type",
  "region_radius",
  "region_type",
  "sort_key",
  "sort_order",
  "min_price",
  "max_price",
  "range_available_price",
  "meta_bedroom_counts",
  "meta_bathroom_counts",
  "region_center",
  "meta_types",
  "meta_unit_types",
  "statuses",
  "features",
  "amenities",
  "available",
  "name",
  "owner",
  "meta_min_lease_duration",
  "meta_max_lease_duration",
  "move_in_month",
  "use_open_search",
  "tags",
  "guarantor",
  "locality",
  "housing_type",
  "amenities",
  "bills_included",
  "amber_sale_status",
];

export const getFilters = (filters = {}, methods = "") => {
  const finalFilters: any = { ...filters };
  const defaultOnly = [
    "id",
    "name",
    "parent_id",
    "pricing",
    "images",
    "videos",
    "location",
    "canonical_name",
    "tags",
    "features",
    "source_link",
    "available",
    "slashed_available_price",
    "nearby_distances",
    "provider_id",
  ];
  const defaultMethods = [
    "active_children_count",
    "location_coordinates",
    "reviews_count",
    "reviews_rating",
    "meta_short",
    "feature_tags",
    "amber_sales",
    "images_with_featured",
    "review_summary",
    // "meta_distances",  // removing for checking latency issues
  ];
  if (hasRoomTypeFilter(finalFilters)) {
    defaultMethods.push("active_room_types");
  }

  // Assign defaults
  finalFilters.p = finalFilters.p || 1;
  finalFilters.limit = finalFilters.limit || 10;

  // Only
  finalFilters.only = finalFilters.only || [];
  if (typeof finalFilters.only === "string") {
    finalFilters.only = finalFilters.only.split(",");
  }
  finalFilters.only.push(defaultOnly);
  finalFilters.only = Array.from(new Set(finalFilters.only));
  finalFilters.only = finalFilters.only.join(",");

  // Methods
  finalFilters.methods = methods;
  finalFilters.methods = finalFilters.methods || [];
  if (typeof finalFilters.methods === "string") {
    finalFilters.methods = finalFilters.methods.split(",");
  }
  finalFilters.methods.push(...defaultMethods);
  finalFilters.methods = Array.from(new Set(finalFilters.methods));
  finalFilters.methods = finalFilters.methods.join(",");

  // Others
  finalFilters.region_radius =
    finalFilters.region_radius ||
    (finalFilters.region?.location?.country?.short_name === "AE" ? 60000 : 20000);
  finalFilters.region_type = finalFilters.region_type || "circle";
  finalFilters.sort_key = finalFilters.sort_key || "relevance_sorting_v2";
  finalFilters.sort_order = finalFilters.sort_order || "desc";
  finalFilters.statuses = finalFilters.statuses || "active";
  finalFilters.use_open_search = true;
  finalFilters.with_active_rooms = true;

  // Remove empty filters
  Object.keys(finalFilters)?.forEach((key: string) => {
    if (
      finalFilters[key] === null ||
      finalFilters[key] === undefined ||
      finalFilters[key] === "" ||
      (Array.isArray(finalFilters[key]) && !finalFilters[key].length)
    ) {
      delete finalFilters[key];
    }
  });
  return finalFilters;
};

export const getFilterParams = (filters = {}, methods = "") => {
  const params: any = {};

  // Build proper filters
  let finalFilters: any = { ...filters };
  finalFilters = getFilters(finalFilters, methods);

  // Build params from Filters
  if (finalFilters) {
    // allowed filters

    // @ts-ignore
    for (const key of filter_keys) {
      if (finalFilters[key] && String(finalFilters[key]).length > 0) {
        params[key] = finalFilters[key];
      }
      if (Array.isArray(params[key])) {
        params[key] = params[key].join(",");
      }
    }

    if (finalFilters.result_count) {
      params.limit = parseInt(finalFilters.result_count, 10);
    }

    // Apply location filters
    if (finalFilters?.region_canonical_name) {
      params.region_canonical_name = finalFilters?.region_canonical_name;
    } else {
      if (
        finalFilters.region &&
        finalFilters.region?.location?.location_coordinates?.lat &&
        finalFilters.region?.location?.location_coordinates?.lng
      ) {
        params.region_lat = finalFilters.region.location.location_coordinates.lat;
        params.region_lng = finalFilters.region.location.location_coordinates.lng;
        params.region_type = "circle";
      }
      if (
        finalFilters.region &&
        finalFilters.region?.location?.location_bounds?.southwest &&
        finalFilters.region?.location?.location_bounds?.northeast
      ) {
        params.region_bounds_lat1 = finalFilters.region.location.location_bounds.southwest.lat;
        params.region_bounds_lng1 = finalFilters.region.location.location_bounds.southwest.lng;
        params.region_bounds_lat2 = finalFilters.region.location.location_bounds.northeast.lat;
        params.region_bounds_lng2 = finalFilters.region.location.location_bounds.northeast.lng;
        params.region_type = "polygon";
      }
      if (
        finalFilters.region &&
        finalFilters.region?.canonical_name &&
        finalFilters.use_open_search
      ) {
        params.region_canonical_name = finalFilters.region.canonical_name;
      }
    }
  }
  return params;
};

export const getAllowedFilters = (filters: any = {}) => {
  const params: any = {};
  if (filters) {
    for (const key of filter_keys) {
      if (filters[key] && String(filters[key]).length > 0) {
        params[key] = filters[key];
      }
      if (Array.isArray(params[key])) {
        params[key] = params[key].join(",");
      }
    }
  }
  const finalFilters = { filters: params };
  return finalFilters;
};

const regionNameCache = new Map();
export const getRegionName = (regionObject: any) => {
  const cacheKey = regionObject?.id;
  if (regionNameCache.has(cacheKey)) {
    return regionNameCache.get(cacheKey);
  }
  const regionName = regionObject?.name || "";
  regionNameCache.set(cacheKey, regionName);
  return regionName;
};

export const getSearchMetaTitle = (meta: any = {}, filters: any = {}, userLanguage = "en") => {
  let title = "";

  // Check for translated meta title first
  if (filters?.region?.meta?.meta_titles_translation?.[userLanguage]?.length > 0) {
    title = filters.region.meta.meta_titles_translation[userLanguage];
  }
  // Fallback to default title
  else if (filters?.region?.meta?.title?.length > 0) {
    title = filters.region.meta.title;
  }
  // Generate default title
  else {
    const regionName = getRegionName(filters.region);
    title = `${config.IS_CHINA_APP ? "学生住宿" : "Student Housing"} ${
      regionName ? `${regionName}` : ""
    }`.trim();
    if (title.length < 35) {
      if (meta && meta.count > 0) {
        title = `${title} | ${meta.count * 100}+ ${config.IS_CHINA_APP ? "房间" : "Rooms"}`;
      } else {
        title += ` | ${config.IS_CHINA_APP ? "最优价格" : "Best Prices"}`;
      }
    }
  }
  return title;
};

export const getSearchMetaDescription = (
  meta: any = {},
  filters: any = {},
  userLanguage = "en",
) => {
  let description = "";

  // Check for translated meta description first
  if (filters?.region?.meta?.meta_descriptions_translation?.[userLanguage]?.length > 0) {
    description = filters.region.meta.meta_descriptions_translation[userLanguage];
  }
  // Fallback to default meta
  else if (filters?.region?.meta?.meta?.length > 0) {
    description = filters.region.meta.meta;
  } else {
    const regionCombinedName = getRegionSelectName(filters.region, ["country"]);
    if (regionCombinedName) {
      description = config.IS_CHINA_APP
        ? `探索学生住宿 ${regionCombinedName}.`
        : `Explore student accommodations in ${regionCombinedName}.`;
    } else {
      description = config.IS_CHINA_APP
        ? "探索校园附近的学生住宿。"
        : "Explore student accommodations near campus.";
    }
    if (meta?.count > 0) {
      description = `${description} ${meta.count * 100}+ Rooms.`;
    }
    description = config.IS_CHINA_APP
      ? `${description} 预订学生公寓和私人宿舍。 ✓ 最优惠的价格 ✓ 免费服务 ✓ 立即咨询！`
      : `${description} Book Student Flats and Private Halls. âœ“ Best Prices âœ“ Free Service âœ“ Enquire Now!`;
  }
  return description;
};

export const getRegionSelectName = (
  regionObject: any,
  region_types = ["sublocality", "locality", "state", "country"],
) => {
  const names = [];
  if (regionObject?.name || regionObject?.name_zh) {
    names.push(regionObject?.name_zh || regionObject.name);
  }
  if (regionObject?.ancestors || regionObject?.ancestors_short) {
    region_types.forEach((region_type) => {
      (regionObject?.ancestors || regionObject?.ancestors_short).forEach((element: any) => {
        if (element.region_type == region_type) {
          names.push(element.name_zh || element.name);
        }
      });
    });
  }
  const name = names.join(", ");
  return name;
};

export const getSearchCanonicalUrl = (
  canonical_name: string,
  filters: any = {},
  userLanguage = "en",
) => {
  // Check for translated canonical tag first
  if (filters?.region?.meta?.canonical_tags_translation?.[userLanguage]?.length > 0) {
    return filters.region.meta.canonical_tags_translation[userLanguage];
  }

  // Fallback to default region_canonical_tag
  if (filters?.region?.meta?.region_canonical_tag?.length > 0) {
    return filters.region.meta.region_canonical_tag;
  }

  // Fall back to default canonical URL generation
  const finalFilters: any = {};
  // Set pagination filters
  if (filters.p) {
    const numericPage = parseInt(filters.p, 10);
    if (!Number.isNaN(numericPage) && numericPage > 1) {
      // default to 1
      finalFilters.p = numericPage;
    }
  }
  // generate final search link
  return getSearchLink(canonical_name, finalFilters);
};

export const getSearchRobotsTag = (filters: any = {}) => {
  // Check if region has custom meta_robots in meta
  if (filters?.region?.meta?.meta_robots?.length > 0) {
    return filters.region.meta.meta_robots;
  }

  // Return null to fall back to default robots tag logic
  return null;
};

export const getSearchLink = (canonical_name: string, filters = {}, regionType = "") => {
  const paths = [];
  if (regionType === "inventory") {
    paths.push("/places");
  } else {
    paths.push("/places/search");
  }
  paths.push(canonical_name);

  // add url options
  const url_options: any = {};
  url_options.path = paths;
  url_options.params = getSearchLinkParams(filters);

  // Build link
  return urlBuilder(url_options.path, { params: url_options.params });
};

// Get params from filter object used to put in the url of search link.
export const getSearchLinkParams = (filters: any = {}) => {
  const finalFilters: any = { ...filters };
  const filterParams = getFilters(finalFilters);
  const defaultFilterParams = getFilters({});

  // remove default params
  Object.keys(defaultFilterParams).forEach((key) => {
    if (filterParams[key] == defaultFilterParams[key]) {
      delete filterParams[key];
    }
  });
  // remove result_count param
  delete filterParams["result_count"];
  delete filterParams["region_canonical_name"];
  // methods is an API field. A room-type filter adds active_room_types, which
  // would otherwise leak the whole methods list into the page URL.
  delete filterParams["methods"];

  if (filterParams["leaseDuration"]) delete filterParams["leaseDuration"];

  if (!filterParams["available"]) {
    delete filterParams["available"];
  }

  // Preserve locality=near-by-{timestamp} in URL params
  // Check if locality includes "near-by" to match the timestamp pattern
  if (finalFilters["locality"] && String(finalFilters["locality"]).includes("near-by")) {
    filterParams["locality"] = finalFilters["locality"];
  }

  // remove location params as it is already included (location place id)
  delete filterParams["region"];
  delete filterParams["location"];
  delete filterParams["token"];
  delete filterParams["location_place_id"];
  return filterParams;
};

export const getInventoryOgImage = (regionObject: any, imageSize = "medium") => {
  const featuredImage = getFeaturedImage(regionObject?.images);
  if (featuredImage?.path) {
    return getImagePath(featuredImage.path, imageSize);
  }
  return null;
};

/** Open Graph is 1.91:1; 1200x630 is what WhatsApp, Slack, X and Facebook all accept. */
const OG_SHARE_IMAGE_PARAMS = { w: 1200, h: 630, fit: "crop", q: 70, fm: "jpg" };

/**
 * Crawler-safe featured image for og:image / twitter:image. Differs from `getInventoryOgImage`
 * in the two ways that decide whether a preview renders at all: big enough to fill a card, and
 * `fm=jpg` pinned with `auto=format` off (the fifth argument) — with it, imgix follows the
 * caller's Accept header and serves AVIF, which no crawler renders.
 *
 * Property page only; BookForm, Payment, ApplicationForm and Search are not shared into chat.
 */
export const getInventoryShareImage = (regionObject: any) => {
  const featuredImage = getFeaturedImage(regionObject?.images);
  if (!featuredImage?.path) return null;
  return getImagePath(featuredImage.path, "", OG_SHARE_IMAGE_PARAMS, false, true) || null;
};

export const moveElementToStart = (arr = [], index: any) => {
  if (index < 0 || index >= arr.length) {
    return "Index out of range";
  }

  const removedElement = arr.splice(index, 1)[0]; // Remove element at given index
  arr.unshift(removedElement); // Add the removed element to the beginning

  return arr;
};

export const saveLastViewedListings = (property: any) => {
  try {
    const ls = window.localStorage;
    if (typeof ls == "undefined") return;
    const key = "INVENTORIES_LAST_VIEWED";
    let array: any = [];
    if (ls[key]) {
      const stringifiedArray = ls[key];
      if (stringifiedArray.length > 0) {
        try {
          array = JSON.parse(stringifiedArray);
          array =
            array?.map((value: any) =>
              typeof value === "object" ? value?.canonical_name : value,
            ) || [];
        } catch (e) {
          localStorage.removeItem(key);
          array = [];
        }
        if (array?.length > 0) {
          if (array.length > 10) {
            array.pop();
          }
          let isAlreadyPresent = false;
          let indexOfElement = 0;
          // eslint-disable-next-line no-plusplus
          for (let i = 0, { length } = array; i < length; ++i) {
            if (array[i] == property.canonical_name) {
              isAlreadyPresent = true;
              indexOfElement = i;
              break;
            }
          }
          if (isAlreadyPresent) {
            array = moveElementToStart(array.slice(), indexOfElement);
          } else {
            array.unshift(property?.canonical_name);
          }
        }
      }
    } else {
      array.push(property?.canonical_name);
    }
    const stringifiedArray = JSON.stringify(array);
    localStorage.setItem(key, stringifiedArray);
    // Covers both the write above and the corrupt-JSON removeItem earlier in this function.
    publishWebState();
  } catch (err) {
    console.log(err);
  }
};

export const getLastViewedListings = (minCount: number, maxCount: number) => {
  const key = "INVENTORIES_LAST_VIEWED";
  let listings = [];

  try {
    listings = JSON.parse(localStorage.getItem(key) || "");
  } catch (e) {
    // A getter that mutates: unparseable JSON is discarded here, so the shell has to be told
    // or it keeps mirroring a list this site has already thrown away.
    localStorage.removeItem(key);
    publishWebState();
    listings = [];
  }

  if (!listings) return [];

  if (minCount) {
    if (listings.length < minCount) {
      return [];
    }
  }
  if (maxCount) {
    if (listings.length > maxCount) {
      listings = listings.slice(0, maxCount);
    }
  }

  const filteredListings =
    listings?.map((value: any) => (typeof value === "object" ? value?.canonical_name : value)) ||
    [];

  return filteredListings;
};

export const sortingKeyExperimentRegionIDs = [
  34, 38, 55, 43, 67, 70, 43, 75, 55, 14, 56, 37, 1838, 426, 492, 490, 424, 422, 498, 734, 500, 90,
  1076, 496, 778, 994, 175, 9, 10, 1728, 2121, 1744,
];
// Nottingham, Glasgow, Liverpool, Leeds
